import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Badge, Table } from 'react-bootstrap';
import { FaSearch, FaCheckCircle, FaUniversity, FaBook, FaCalendar } from 'react-icons/fa';
import { toast } from 'react-toastify';
import BatchService from '../../services/batchService';
import StudentService from '../../services/studentService';

const AssignToBatchModal = ({ show, onHide, student, onSuccess }) => {
    const [batches, setBatches] = useState([]);
    const [filteredBatches, setFilteredBatches] = useState([]);
    const [assignedBatches, setAssignedBatches] = useState([]);
    const [enrolledCourseIds, setEnrolledCourseIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loadingAssigned, setLoadingAssigned] = useState(false);
    const [loadingCourses, setLoadingCourses] = useState(false);
    const [dropdownLoaded, setDropdownLoaded] = useState(false);

    useEffect(() => {
        if (show && student) {
            loadBatches();
            loadAssignedBatches();
            loadEnrolledCourses();
            // Reset dropdown state when modal opens
            setShowDropdown(false);
            setSelectedBatch(null);
            setSearchTerm('');
            setDropdownLoaded(false);
        }
    }, [show, student]);

    useEffect(() => {
        // Only filter when we have batches loaded
        if (batches.length > 0) {
            filterBatches();
        }
    }, [searchTerm, batches, enrolledCourseIds]);

    const loadBatches = async () => {
        try {
            setLoading(true);
            const response = await BatchService.getAllBatches();
            const allBatches = response.data || [];
            setBatches(Array.isArray(allBatches) ? allBatches : []);
        } catch (error) {
            console.error('Failed to load batches:', error);
            toast.error('Failed to load batches');
            setBatches([]);
        } finally {
            setLoading(false);
        }
    };

    const loadAssignedBatches = async () => {
        try {
            setLoadingAssigned(true);
            const response = await StudentService.getStudentBatches(student.id);
            const batchesData = response?.data || [];
            setAssignedBatches(Array.isArray(batchesData) ? batchesData : []);
        } catch (error) {
            console.error('Failed to load assigned batches:', error);
            setAssignedBatches([]);
        } finally {
            setLoadingAssigned(false);
        }
    };

    // Load courses the student is already enrolled in
    const loadEnrolledCourses = async () => {
        try {
            setLoadingCourses(true);
            const response = await StudentService.getStudentDetails(student.id);
            const courses = response?.data?.courses || [];
            const courseIds = courses.map(course => course.course_id);
            console.log('Enrolled Course IDs:', courseIds);
            setEnrolledCourseIds(courseIds);
        } catch (error) {
            console.error('Failed to load enrolled courses:', error);
            setEnrolledCourseIds([]);
        } finally {
            setLoadingCourses(false);
        }
    };

    const filterBatches = () => {
        if (!Array.isArray(batches)) {
            setFilteredBatches([]);
            return;
        }
        
        console.log('All Batches:', batches.length);
        console.log('Enrolled Course IDs:', enrolledCourseIds);
        
        // Filter out batches for courses the student is already enrolled in
        let availableBatches = batches.filter(batch => {
            const isEnrolled = enrolledCourseIds.includes(batch.course_id);
            if (isEnrolled) {
                console.log(`Filtering out batch ${batch.name} - Course already enrolled`);
            }
            return !isEnrolled;
        });
        
        console.log('Available Batches after filter:', availableBatches.length);
        
        // FIXED: Show ALL available batches when no search term
        if (!searchTerm.trim()) {
            // Show ALL available batches, not just 3
            setFilteredBatches(availableBatches);
        } else {
            const filtered = availableBatches.filter(batch =>
                batch.name && batch.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredBatches(filtered);
        }
    };

    const handleSelectBatch = (batch) => {
        setSelectedBatch(batch);
        setSearchTerm(batch.name);
        setShowDropdown(false);
    };

    const handleSubmit = async () => {
        if (!selectedBatch) {
            toast.error('Please select a batch');
            return;
        }

        if (enrolledCourseIds.includes(selectedBatch.course_id)) {
            toast.error(`Student is already enrolled in this course. Cannot assign to another batch for the same course.`);
            return;
        }

        try {
            setSubmitting(true);
            const response = await StudentService.assignToBatch(student.id, selectedBatch.id);
            toast.success(response.message || 'Student assigned to batch and verified successfully');
            onSuccess();
            onHide();
            resetForm();
        } catch (error) {
            console.error('Failed to assign:', error);
            toast.error(error.response?.data?.message || 'Failed to assign student to batch');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setSearchTerm('');
        setSelectedBatch(null);
        setShowDropdown(false);
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Get count of available batches (not enrolled)
    const getAvailableBatchesCount = () => {
        if (!Array.isArray(batches)) return 0;
        return batches.filter(batch => !enrolledCourseIds.includes(batch.course_id)).length;
    };

    if (!student) return null;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaUniversity className="me-2 text-primary" />
                    Assign Student to Batch
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Student Information */}
                <div className="bg-light p-3 rounded mb-4">
                    <h6 className="mb-3">Student Information</h6>
                    <div className="row">
                        <div className="col-md-6">
                            <p className="mb-1"><strong>Name:</strong> {student.name}</p>
                            <p className="mb-1"><strong>Email:</strong> {student.email}</p>
                        </div>
                        <div className="col-md-6">
                            <p className="mb-1"><strong>Phone:</strong> {student.phone}</p>
                            <p className="mb-1">
                                <strong>Registered:</strong> {formatDate(student.created_at)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Already Assigned Batches */}
                {Array.isArray(assignedBatches) && assignedBatches.length > 0 && (
                    <div className="mb-4">
                        <h6 className="mb-2">📚 Already Enrolled Batches ({assignedBatches.length})</h6>
                        <div className="border rounded p-2 bg-light">
                            <Table size="sm" bordered className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Batch Name</th>
                                        <th>Course</th>
                                        <th>Start Date</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {assignedBatches.map((batch, idx) => (
                                        <tr key={idx}>
                                            <td><strong>{batch.batch_name}</strong></td>
                                            <td>{batch.course_name}</td>
                                            <td>{formatDate(batch.start_date)}</td>
                                            <td>
                                                <Badge bg={batch.batch_status === 'active' ? 'success' : 'secondary'}>
                                                    {batch.batch_status || 'Active'}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        <small className="text-muted">Student can be assigned to batches for different courses.</small>
                    </div>
                )}

                {/* Batch Selection */}
                <Form.Group className="mb-3">
                    <Form.Label>Select New Batch <span className="text-danger">*</span></Form.Label>
                    <div className="position-relative">
                        <div className="input-group">
                            <span className="input-group-text">
                                <FaSearch />
                            </span>
                            <Form.Control
                                type="text"
                                placeholder="Search batch by name..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setShowDropdown(true);
                                }}
                                onFocus={() => {
                                    setShowDropdown(true);
                                }}
                            />
                            {selectedBatch && (
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => {
                                        setSelectedBatch(null);
                                        setSearchTerm('');
                                        setShowDropdown(true);
                                    }}
                                >
                                    Clear
                                </Button>
                            )}
                        </div>
                        
                        {showDropdown && (
                            <div className="dropdown-menu show w-100 mt-1" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                {loading ? (
                                    <div className="dropdown-item text-center">
                                        <Spinner animation="border" size="sm" />
                                        <span className="ms-2">Loading batches...</span>
                                    </div>
                                ) : filteredBatches.length === 0 ? (
                                    <div className="dropdown-item text-muted text-center">
                                        {searchTerm 
                                            ? `No batches found matching "${searchTerm}"` 
                                            : getAvailableBatchesCount() === 0
                                                ? 'Student is already enrolled in all available courses.'
                                                : 'No batches available'
                                        }
                                    </div>
                                ) : (
                                    <>
                                        {/* Show count of results */}
                                        <div className="dropdown-item text-muted border-bottom">
                                            <small>Found {filteredBatches.length} batch(es)</small>
                                        </div>
                                        {filteredBatches.map(batch => (
                                            <button
                                                key={batch.id}
                                                className={`dropdown-item ${selectedBatch?.id === batch.id ? 'active' : ''}`}
                                                onClick={() => handleSelectBatch(batch)}
                                                type="button"
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <strong>{batch.name}</strong>
                                                        <br />
                                                        <small className="text-muted">
                                                            <FaBook className="me-1" size={10} />
                                                            {batch.course_name || 'N/A'} | 
                                                            <FaCalendar className="ms-2 me-1" size={10} />
                                                            Start: {formatDate(batch.start_date)}
                                                        </small>
                                                    </div>
                                                    {selectedBatch?.id === batch.id && (
                                                        <FaCheckCircle className="text-success" />
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    <Form.Text className="text-muted">
                        {getAvailableBatchesCount()} batch(es) available for new courses. Type to search.
                    </Form.Text>
                </Form.Group>

                {/* Selected Batch Preview */}
                {selectedBatch && (
                    <Alert variant="success" className="mt-2">
                        <FaCheckCircle className="me-2" />
                        <strong>Selected:</strong> {selectedBatch.name}
                        <br />
                        <small>Course: {selectedBatch.course_name || 'N/A'} | Start Date: {formatDate(selectedBatch.start_date)}</small>
                    </Alert>
                )}

                {/* What will happen */}
                <Alert variant="info" className="mt-3">
                    <strong>After assigning:</strong>
                    <ul className="mb-0 mt-2">
                        <li>✅ Student will be added to the selected batch</li>
                        {student.status === 'pending' && <li>✅ Student will be automatically verified</li>}
                        <li>✅ Student will be enrolled in the course</li>
                        <li>✅ Payment tracking will be created</li>
                    </ul>
                </Alert>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={submitting}>
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSubmit} 
                    disabled={!selectedBatch || submitting}
                >
                    {submitting ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Assigning...
                        </>
                    ) : (
                        <>
                            <FaCheckCircle className="me-2" />
                            Confirm & Assign
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AssignToBatchModal;